<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div ref="boxRef" class="teacher-advice-box" :class="{ visible: show }">
    <img
      src="@/assets/report/teacher.png"
      alt="老师"
      class="teacher-img"
    />
    <div ref="adviceScrollRef" class="advice-content">
      <span class="advice-prefix">老师建议:</span>
      <div v-if="adviceLoading" class="ai-loading-container">
        <div class="loading-dots">
          <div class="dot"></div>
          <div class="dot"></div>
          <div class="dot"></div>
        </div>
      </div>
      <div
        v-show="!adviceLoading"
        class="advice-markdown"
        v-html="renderedAdvice"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { marked } from "marked";
import "katex/dist/katex.min.css";
import { parseReportDetail } from "@/api/aiDictationModule.js";
import { savePreciseAiAnalysisReport } from "@/api/precise.js";

const props = defineProps({
  teacherAdvice: { type: String, default: "" },
  aiSubmitContent: { type: String, default: "" },
  /** AI 服务 token，须先调 aiAuth；用于 parsePrompt 的 X-Authorization-Token */
  aiToken: { type: String, default: "" },
  /** getPreciseReport 返回的 reportId，用于保存 AI 分析 */
  reportId: { type: String, default: "" },
  /** 报告已有 analysis 时跳过 parsePrompt 流式与保存 */
  skipAiStream: { type: Boolean, default: false },
});

marked.setOptions({
  gfm: true,
  breaks: true,
  headerIds: true,
  mangle: false,
  headerPrefix: "",
  sanitize: false,
  smartLists: true,
  xhtml: true,
});

function renderMath(text) {
  try {
    let safeText = typeof text === "string" ? text : String(text ?? "");
    safeText = safeText.replace(/\\n/g, "\n");
    safeText = safeText.replace(/\$\$\n/g, "$$");
    safeText = safeText.replace(/([^\\])_(?!_)/g, "$1\\_");
    safeText = safeText.replace(/^_(?!_)/gm, "\\_");
    const html = marked.parse(safeText);
    let finalHtml = html.replace(/<del>/g, "<u>").replace(/<\/del>/g, "</u>");
    finalHtml = finalHtml.replace(/<s>/g, "<u>").replace(/<\/s>/g, "</u>");
    return `<div class="ai-message-box markdown-body">${finalHtml}</div>`;
  } catch (e) {
    console.error("老师建议 Markdown 渲染失败:", e);
    return `<div class="ai-message-box markdown-body">${text || ""}</div>`;
  }
}

const show = ref(false);
const boxRef = ref(null);
const adviceScrollRef = ref(null);
const streamedText = ref("");
const streamStarted = ref(false);
const adviceLoading = ref(false);
const adviceSaved = ref(false);
let observer = null;
let streamCtrl = null;

const renderedAdvice = computed(() => {
  const raw =
    streamedText.value.length > 0 ? streamedText.value : props.teacherAdvice || "";
  if (!String(raw).trim()) {
    return '<div class="ai-message-box markdown-body"></div>';
  }
  return renderMath(raw);
});

function buildParseBody(promptVal) {
  if (promptVal === undefined || promptVal === null) return null;
  const prompt = typeof promptVal === "string" ? promptVal : String(promptVal);
  if (!prompt.trim()) return null;
  return { prompt };
}

function scrollAdviceToBottom() {
  nextTick(() => {
    const el = adviceScrollRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

function startStreamIfNeeded() {
  if (props.skipAiStream) return;
  if (streamStarted.value) return;
  const token = props.aiToken;
  if (!token || !String(token).trim()) return;
  const body = buildParseBody(props.aiSubmitContent);
  if (!body) return;
  streamStarted.value = true;
  streamedText.value = "";
  adviceLoading.value = true;
  adviceSaved.value = false;
  streamCtrl = parseReportDetail(
    body,
    (chunk) => {
      if (chunk == null) return;
      if (adviceLoading.value) adviceLoading.value = false;
      streamedText.value += String(chunk);
      scrollAdviceToBottom();
    },
    token,
    async () => {
      adviceLoading.value = false;
      scrollAdviceToBottom();
      const text = streamedText.value.trim();
      if (!text || adviceSaved.value) return;
      adviceSaved.value = true;
      try {
        await savePreciseAiAnalysisReport({
          analysis: streamedText.value,
          reportId: props.reportId,
        });
      } catch (e) {
        console.error("老师评价保存失败:", e);
        adviceSaved.value = false;
      }
    }
  );
  streamCtrl.catch((err) => {
    console.error("老师建议流式解析失败:", err);
    adviceLoading.value = false;
    streamStarted.value = false;
  });
}

watch(
  () => [props.aiSubmitContent, props.aiToken, props.skipAiStream],
  () => {
    if (props.skipAiStream) return;
    startStreamIfNeeded();
  },
  { deep: true, immediate: true }
);

watch(
  () => props.skipAiStream,
  (skip) => {
    if (!skip) return;
    streamCtrl?.abort?.();
    streamCtrl = null;
    if (streamStarted.value || adviceLoading.value) {
      streamStarted.value = false;
      adviceLoading.value = false;
    }
  }
);

watch(
  () => props.teacherAdvice,
  () => {
    if (!streamedText.value.length) scrollAdviceToBottom();
  }
);

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting) show.value = true;
    },
    { threshold: 0.2 }
  );
  if (boxRef.value) observer.observe(boxRef.value);
});

onBeforeUnmount(() => {
  if (observer && boxRef.value) observer.unobserve(boxRef.value);
  streamCtrl?.abort?.();
});
</script>

<style scoped lang="scss">
.teacher-advice-box {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  margin-top: 15px;

  .teacher-img {
    width: 150px;
    height: 150px;
    flex-shrink: 0;
    opacity: 0;
    transform: scale(0.9);
    transition: opacity 0.5s ease-out, transform 0.5s ease-out;
  }

  .advice-content {
    flex: 1;
    min-width: 0;
    max-width: 710px;
    min-height: calc(30px + 8px + 1.6em + 1.6em);
    overflow-x: hidden;
    background: #ffffff;
    box-shadow: 0px 2px 5px 1px rgba(160, 125, 255, 0.3);
    border-radius: 20px;
    padding: 15px;
    box-sizing: border-box;
    font-size: 14px;
    color: #666666;
    line-height: 1.6;
    opacity: 0;
    transition: opacity 0.5s ease-out;

    .ai-loading-container {
      .loading-dots {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 4px 0 0;

        .dot {
          border-radius: 50%;
          animation: advice-dots-bounce 1.4s infinite ease-in-out;

          &:nth-child(1) {
            width: 10px;
            height: 10px;
            background: #a07dff;
            animation-delay: -0.32s;
          }

          &:nth-child(2) {
            width: 14px;
            height: 14px;
            background: #a07dff;
            opacity: 0.8;
            animation-delay: -0.16s;
          }

          &:nth-child(3) {
            width: 16px;
            height: 16px;
            background: #a07dff;
            opacity: 0.6;
          }
        }
      }
    }
  }

  .advice-prefix {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    color: #666666;
  }

  .advice-markdown {
    user-select: text;
    -webkit-user-select: text;
  }

  &.visible {
    .teacher-img {
      opacity: 1;
      transform: scale(1);
    }

    .advice-content {
      opacity: 1;
      transition-delay: 0.15s;
    }
  }
}

@keyframes advice-dots-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}

:deep(.ai-message-box) {
  width: 100%;
  word-break: break-word;
  position: relative;
  background: transparent;
  padding: 0;
  border-radius: 0;
  display: block;

  * {
    font-size: inherit;
  }

  .katex {
    font-size: 1.1em;
  }

  .katex-display {
    margin: 1em 0;
    text-align: center;
  }

  u {
    text-decoration: underline !important;
  }

  del,
  s {
    text-decoration: line-through !important;
  }
}

:deep(.markdown-body) {
  color: #24292e;
  line-height: 1.6;

  code {
    padding: 0.2em 0.4em;
    background-color: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
  }

  pre {
    padding: 16px;
    background-color: #f6f8fa;
    border-radius: 6px;
  }

  u {
    text-decoration: underline !important;
  }

  del,
  s {
    text-decoration: line-through !important;
  }
}
</style>
